import { useState, useEffect } from 'react';
import { IoEyeOutline, IoDownloadOutline, IoWarningOutline } from 'react-icons/io5';
import { MdEdit, MdDelete, MdDriveFileMove, MdShare, MdMoreVert } from 'react-icons/md';
import { BsFileEarmarkPdf, BsFileEarmarkWord, BsFileEarmarkExcel, BsFileEarmarkPpt, BsFileEarmarkImage, BsFileEarmarkPlay, BsFileEarmark } from 'react-icons/bs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import Divider from '@mui/material/Divider';
import * as mime from 'react-native-mime-types';
import fileDownload from 'js-file-download';
import moment from 'moment';
import DOMPurify from 'dompurify';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import DeleteFileForm from '@CommonFunctionalDisplay/DeleteFileForm';
import FileService from '@Helpers/URLHelpers/FileService';
import ErrorHandler from '@Root/ErrorHandler';
import './FileRow.scss';

export default function FileRow(props) {

    const [cursor, setCursor] = useState('auto');
    const [fileName, setFileName] = useState(props?.fileName || 'Unknown File');
    const [imageUrl, setImageUrl] = useState('');
    const [loadError, setLoadError] = useState(false);
    const [fileData, setFileData] = useState(null);
    const [showFileDeleteForm, setShowFileDeleteForm] = useState(false);
    const [showFileNameInput, setShowFileNameInput] = useState(false);
    const [showInfoOptions, setShowInfoOptions] = useState(false);
    const [anchorInfoMenu, setAnchorInfoMenu] = useState(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [permission, setPermission] = useState(props.fileData?.permission);
    const [showFilePermission, setShowFilePermission] = useState(false);
    const [showMigrateFileForm, setShowMigrateFileForm] = useState(false);
    const [isCorrupted, setIsCorrupted] = useState(false);
    const [mimeType, setMimeType] = useState('');
    const { currentProjectId } = useReduxProject();
    const { currentLeadsId } = useReduxLeads();
    const fileService = new FileService();
    const errorHandler = new ErrorHandler();
    const className = 'fileRow';

    useEffect(() => {
        parseData();
    }, [])

    const parseData = async() => {
        try {
            setFileName(props?.fileName || 'Unknown File');

            // Validate mimeType
            const lookupResult = mime.lookup(props?.fileName);

            if (typeof lookupResult !== 'string' || !lookupResult) {
                console.error('Invalid MIME type for file:', props?.fileName);
                setIsCorrupted(true);
                setMimeType('corrupted');
                return;
            }

            setMimeType(lookupResult);
            setFileData(props?.fileData);

            if(lookupResult.split('/')[0] === 'image') {
                await renderPreview();
            }
        } catch (error) {
            console.error('Error parsing file data:', error);
            errorHandler.handleError(error);
            setIsCorrupted(true);
            setMimeType('corrupted');
        }
    }

    const renderPreview = async() => {
        try {
            let url = props?.previewImgUrl;
            if(url === '' || url === undefined) {
                let fileRes = await fileService.getCompressedFileById(props?.fileId);
                fileRes = JSON.parse(fileRes.body);
                fileRes = JSON.parse(fileRes?.fileClob);
                url = fileRes.file;
            }
            setImageUrl(url);
        } catch (err) {
            console.error('Error rendering preview:', err);
            errorHandler.handleError(err);
            setLoadError(true);
        }
    }

    const handleContextMenu = (event) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                }
                : null,
        );
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const dataURLtoFile = (dataurl) => {
        try {
            var arr = dataurl?.split(','),
                mime = arr[0]?.match(/:(.*?);/)[1],
                bstr = atob(arr[1]),
                n = bstr.length,
                u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new File([u8arr], { type: mime });
        } catch (error) {
            throw new Error('Failed to process file data');
        }
    };

    const downloadFile = async() => {
        if (isCorrupted) {
            alert('Cannot download corrupted file');
            return;
        }
        setCursor('wait');
        try {
            let fileObj = await fileService.getFile(props?.fileId);
            fileObj = JSON.parse(fileObj.body);
            fileDownload(dataURLtoFile(JSON.parse(fileObj?.fileClob)?.file), JSON.parse(fileObj?.fileClob)?.fileName);
        } catch (err) {
            errorHandler.handleError(err);
            alert('Failed to download file. The file may be corrupted.');
        }
        handleOptionsPopup();
        handleCloseContextMenu();
        setCursor('auto');
    }

    const handleOptionsPopup = (event) => {
        if(!showInfoOptions){
            if(!!event) {
                setAnchorInfoMenu(event.currentTarget);
                setShowInfoOptions(true);
            }
        } else {
            setAnchorInfoMenu(null);
            setShowInfoOptions(false);
        }
    }

    const handleRenameFile = async() => {
        toggleShowFileNameInput();
        if(fileName === props.fileName) return;
        setCursor('wait');
        try {
            let name = fileName.split('.');
            if(name.length !== 1) {
                name.length = name.length - 1;
                name = name.join('');
            }else {
                name = name.join('');
            }
            let fileType = props?.fileName.split('.')[1];
            let parentType = 'LEADS_WHATSAPP'
            let parentId = currentLeadsId;
            let bodyObj = {
                parentId: parentId,
                parentType: parentType,
                fileId: props.fileId,
                fileName: name + '.' + fileType,
            }
            let response = await fileService.renameFileName(bodyObj);
            if(response.body === 'true') {
                setFileName(bodyObj.fileName);
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleFileNameChange = (e)=> {
        let sanitizedFileName = DOMPurify.sanitize(e.target.value);
        setFileName(sanitizedFileName);
    }

    const toggleShowFileNameInput = ()=> {
        setShowFileNameInput(!showFileNameInput);
        handleOptionsPopup();
        handleCloseContextMenu();
    }

    const toggleShowFileDeleteForm = ()=> {
        setShowFileDeleteForm(!showFileDeleteForm);
        handleOptionsPopup();
        handleCloseContextMenu();
    }

    const toggleShowFilePermission = ()=> {
        setShowFilePermission(!showFilePermission);
        handleOptionsPopup();
        handleCloseContextMenu();
    }

    const toggleShowMigrateFileForm = ()=> {
        setShowMigrateFileForm(!showMigrateFileForm);
        handleOptionsPopup();
        handleCloseContextMenu();
    }

    const getFileIcon = () => {
        if (isCorrupted) return <IoWarningOutline />;
        if (typeof mimeType !== 'string') return <BsFileEarmark />;
        
        try {
            if (mimeType.indexOf('image') > -1) return <BsFileEarmarkImage />;
            if (mimeType.indexOf('video') > -1) return <BsFileEarmarkPlay />;
            if (mimeType.indexOf('pdf') > -1) return <BsFileEarmarkPdf />;
            if (mimeType.indexOf('officedocument.wordprocessingml') > -1) return <BsFileEarmarkWord />;
            if (mimeType.indexOf('officedocument.spreadsheetml') > -1) return <BsFileEarmarkExcel />;
            if (mimeType.indexOf('application/vnd.ms-powerpoint') > -1) return <BsFileEarmarkPpt />;
            return <BsFileEarmark />;
        } catch (error) {
            console.error('Error determining file icon:', error);
            return <IoWarningOutline />;
        }
    };

    const getFileTypeLabel = () => {
        if (isCorrupted) return 'Corrupted';
        if (typeof mimeType !== 'string') return 'Unknown';
        
        try {
            if (mimeType.indexOf('image') > -1) return 'Image';
            if (mimeType.indexOf('video') > -1) return 'Video';
            if (mimeType.indexOf('pdf') > -1) return 'PDF';
            if (mimeType.indexOf('officedocument.wordprocessingml') > -1) return 'Word';
            if (mimeType.indexOf('officedocument.spreadsheetml') > -1) return 'Excel';
            if (mimeType.indexOf('application/vnd.ms-powerpoint') > -1) return 'PowerPoint';
            const ext = fileName?.split('.').pop()?.toUpperCase();
            return ext || 'File';
        } catch (error) {
            console.error('Error determining file type:', error);
            return 'Unknown';
        }
    };

    const getFileSize = () => {
        const size = props?.fileSize || props?.fileData?.fileSize;
        if (!size || size === 'Unknown') return 'Unknown';
        
        if (typeof size === 'number') {
            if (size < 1) return `${(size * 1024).toFixed(0)} KB`;
            return `${size.toFixed(2)} MB`;
        }
        return size;
    };

    return (
        <Box 
            className={`${className} ${isCorrupted ? className + '--corrupted' : ''}`}
            style={{cursor: cursor}}
            onContextMenu={handleContextMenu}>
            <Box className={className + '__container'}>
                {/* Left Section - Icon & Name */}
                <Box className={className + '__left'}>
                {isCorrupted ? (
                    <Box className={`${className}__preview__icon ${className}__preview__icon--corrupted`}>
                        <IoWarningOutline />
                    </Box>
                ) : typeof mimeType === 'string' && mimeType.indexOf('image') > -1 ? (
                    imageUrl && !loadError ? (
                        <img
                            className={className + '__preview__image'}
                            src={imageUrl}
                            alt={fileName}
                            onError={() => setLoadError(true)}
                        />
                    ) : (
                        <Box className={className + '__preview__icon'}>
                            {loadError ? <IoWarningOutline /> : getFileIcon()}
                        </Box>
                    )
                ) : (
                    <Box className={className + '__preview__icon'}>
                        {getFileIcon()}
                    </Box>
                )}
                    <Box className={className + '__info'}>
                        {showFileNameInput ? (
                            <TextField
                                placeholder='Rename file'
                                className={className + '__info__input'}
                                type='text'
                                value={fileName}
                                onChange={handleFileNameChange}
                                onBlur={handleRenameFile}
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        handleRenameFile();
                                    }
                                }}
                                variant='standard'
                                autoFocus
                                size="small"
                                disabled={isCorrupted}
                            />
                        ) : (
                            <>
                                <Typography variant="subtitle2" className={className + '__name'}>
                                    {fileName}
                                </Typography>
                                {isCorrupted && (
                                    <Typography variant="caption" className={className + '__corrupted-label'}>
                                        Corrupted file
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                </Box>

                {/* Middle Section - Type, Size & Date */}
                <Box className={className + '__middle'}>
                    <Typography variant="caption" className={className + '__type'}>
                        {getFileTypeLabel()}
                    </Typography>
                    <Typography variant="caption" className={className + '__size'}>
                        {getFileSize()}
                    </Typography>
                    <Typography variant="caption" className={className + '__date'}>
                        {props.fileData?.createTimeStamp 
                            ? moment(props.fileData.createTimeStamp).format('MMM DD, YYYY')
                            : 'Unknown date'}
                    </Typography>
                </Box>

                {/* Right Section - Actions */}
                <Box className={className + '__right'}>
                    <Tooltip title="Download" arrow placement="left" slots={{ transition: Zoom }}>
                        <IconButton 
                            className={className + '__action'}
                            size="small"
                            onClick={downloadFile}
                            disabled={isCorrupted}>
                            <IoDownloadOutline />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="More" arrow placement="left" slots={{ transition: Zoom }}>
                        <IconButton 
                            className={className + '__menu'}
                            size="small"
                            onClick={handleOptionsPopup}
                            disabled={isCorrupted && !permission?.allowDelete}>
                            <MdMoreVert />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            {/* Options Menu (Three Dots) */}
            {showInfoOptions && (
                <Menu
                    open={showInfoOptions}
                    onClose={handleOptionsPopup}
                    anchorEl={anchorInfoMenu}
                    className={className + '__dropdown'}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                    <MenuItem 
                        className={className + '__dropdown__item'}
                        onClick={downloadFile}
                        disabled={isCorrupted}>
                        <IoDownloadOutline className={className + '__dropdown__item__icon'} />
                        <Typography>Download</Typography>
                    </MenuItem>
                    <MenuItem 
                        className={className + '__dropdown__item'}
                        onClick={toggleShowFileNameInput}
                        disabled={true}>
                        <MdEdit className={className + '__dropdown__item__icon'} />
                        <Typography>Rename</Typography>
                    </MenuItem>
                    <MenuItem 
                        className={`${className}__dropdown__item ${className}__dropdown__item--danger`}
                        onClick={toggleShowFileDeleteForm}>
                        <MdDelete className={className + '__dropdown__item__icon'} />
                        <Typography>Delete</Typography>
                    </MenuItem>
                </Menu>
            )}
            {
                showFileDeleteForm && (
                    <DeleteFileForm
                        open={showFileDeleteForm}
                        closePopup={toggleShowFileDeleteForm}
                        fileName={props.fileName}
                        fileId={props.fileId}
                        renderData={props.renderData}
                    />
                )
            }
        </Box>
    );
}